import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios";
import * as Endpoints from './endpoints';
import BaseService from "./base-service";
import TokenStorage from "./token-storage";
import {SecurityContext} from "../types/SecurityContext";
import {getUserInfo} from "../utils/jwt-utils";
import {DialogService} from "../common/Dialog";
import SomlRbacService from "./soml-rbac-service";
import {RbacRole} from "../types/Rbac";
import SecurityContextStorage from "./security-context-storage";
import * as RbacUtils from "../utils/rbac-utils";
import * as LocationUtils from "../utils/location-utils";
import {AUTHORIZATION} from "./headers";

/**
 * Service for authentication and authorization.
 */
class SecurityService extends BaseService {

  public static readonly REASONS = {
    SHOULD_LOGIN: "SHOULD_LOGIN"
  }

  /**
   * Initializes and returns a {@link SecurityContext}.
   *
   * If the security is enabled and there are no access tokens, the service will redirect to
   * the login page.
   *
   * In case of existing tokens, the service will resolve the current user information and roles.
   *
   * @returns {Promise<SecurityContext>}
   */
  static initSecurity(): Promise<SecurityContext> {
    return SecurityService.loadSecurityContext().then((securityContext: SecurityContext) => {
      if (securityContext.enabled) {
        // Add an interceptor to refresh the access token if it expires
        SecurityService.registerRefreshInterceptor();
        return this.login().then(() => this.updateContext());
      }
      return securityContext;
    });
  }

  private static loadSecurityContext(): Promise<SecurityContext> {
    return axios.get(Endpoints.SECURITY)
      .then(this.resolveResponse)
      .then((data: any) => {
        // Cache the configuration for later when doing login/logout
        return SecurityContextStorage.initialize({
          enabled: data.enabled,
          loginUrl: data.loginUrl,
          logoutUrl: data.logoutUrl,
          user: null,
          roles: null
        });
      })
      .catch(this.resolveErrorResponse);
  }

  private static login() {
    // If we have an authentication code -> use it for access and refresh tokens
    const authCode = SecurityService.getAuthenticationCode();
    if (authCode) {
      // Remove leftovers from Fusion's redirect
      // Note: This is bad for bookmarkable URLs but when Fusion redirects, we cannot preserve them anyway
      window.history.replaceState({}, document.title, LocationUtils.getCleanUrl());
      return SecurityService.authenticate(authCode);
    }

    // If we don't have an auth code and we don't have either access or refresh token -> login
    if (!TokenStorage.hasTokens()) {
      return SecurityService.openLogin();
    }

    // Assume we don't have to login in if we have tokens
    // However, if the current JWT has expired, the interceptor will refresh it
    this.setAxiosClientDefaultAuthHeader(TokenStorage.getAccessToken());
    return Promise.resolve();
  }

  private static updateContext() {
    return Promise.resolve(this.resolveUserInfo())
      .then(() => this.resolveRbac())
      .then(() => SecurityContextStorage.getContext());
  }

  private static resolveUserInfo() {
    SecurityContextStorage.getContext().user = getUserInfo(TokenStorage.getAccessToken());
  }

  private static resolveRbac() {
    return SomlRbacService.getUserRoles().then((roles: Array<RbacRole>) => {
      const context = SecurityContextStorage.getContext();
      context.roles = roles;
      if (context.user) {
        context.user.permissions = RbacUtils.getUserPermissions(context);
      }
    });
  }

  static logout() {
    TokenStorage.clear();
    const context = SecurityContextStorage.getContext();
    if (context.logoutUrl) {
      window.location.href = context.logoutUrl;
    }
  }

  private static registerRefreshInterceptor() {
    axios.interceptors.response.use((response) => response, (error) => {
      // If the status is HTTP 401 Unauthorized, try to get new tokens or re-login
      if (SecurityService.isUnauthorized(error)) {

        const originalRequestConfig = error.config;

        // If a request to the security API failed then we cannot do anything else but reject
        if (originalRequestConfig.url.startsWith(Endpoints.SECURITY)) {
          return Promise.reject(error);
        }

        // Avoids an infinite loop of intercept/refresh
        if (originalRequestConfig._isRetry === undefined) {
          originalRequestConfig._isRetry = 0
        } else if (originalRequestConfig._isRetry > 1) {
          return Promise.reject(error);
        } else {
          originalRequestConfig._isRetry++;
        }

        // If we have a refresh token -> obtain new access token
        if (TokenStorage.hasRefreshToken()) {
          return SecurityService.refresh(() => {
            // Update original request with the new token
            // Changing axios default headers has no effect here because it already contains that header and is not overridden
            SecurityService.setRequestAuthHeader(originalRequestConfig, TokenStorage.getAccessToken());
            // Retry the original request if new access token was granted
            return axios(originalRequestConfig);
          });
        }

        // No refresh token -> go login
        return SecurityService.openLogin();
      }

      // All other HTTP errors are not handled.
      return Promise.reject(error);
    });
  }

  private static openLogin() {
    const context = SecurityContextStorage.getContext();
    if (context.loginUrl) {
      window.location.href = context.loginUrl;
    }
    return Promise.reject({
      reason: SecurityService.REASONS.SHOULD_LOGIN
    });
  }

  private static getAuthenticationCode(): string | null {
    const search = window.location.search;
    if (search) {
      return new URLSearchParams(search).get('code');
    }
    return null;
  }

  private static authenticate(code: string) {
    // Exchange the authentication code for access(JWT) and refresh tokens
    return axios.get(Endpoints.SECURITY_AUTHENTICATE, {
      params: {
        code
      }
    }).then(response => this.setTokens(response))
      .catch((error) => {
        // Couldn't authenticate (old auth code or else) -> login
        return this.onFailedAuth(error);
      });
  }

  /**
   * Refreshes the access token used for authentication header for the secured requests to the services.
   * Provides an option to retry the original request, after the token is refreshed. Additionally the method
   * will refresh the tokens in the {@link TokenStorage} so that they are up to date. Finally if the requests
   * are completed without errors, the current {@link SecurityContext} will be updated as well.
   *
   * @param retryRequestCallback function that allows retry of the original request, after token refresh
   */
  static refresh(retryRequestCallback: () => Promise<any>) {
    return axios.get(Endpoints.SECURITY_REFRESH, {
      params: {
        accessToken: TokenStorage.getAccessToken(),
        refreshToken: TokenStorage.getRefreshToken(),
      }
    }).then((response) => {
      // Note: this response contains only the new access token, refresh is not provided
      this.setTokens(response);
      return retryRequestCallback();
    }).then((response) => {
      // Recalculate User info and RBAC role bindings.
      // Done in case the user has changes in its details and/or roles between sessions.
      return SecurityService.updateContext().then(() => response);
    }).catch((error) => {
      // Couldn't obtain new access token (error, expired or else) -> login
      if (SecurityService.isUnauthorized(error)) {
        return this.onFailedAuth(error);
      }
      // The request has failed for other reasons (400, 403, 5xx etc.)
      return Promise.reject(error);
    });
  }

  private static setTokens(response: AxiosResponse) {
    const accessToken = response.data.tokens.access;
    TokenStorage.saveAccessToken(accessToken);

    const refreshToken = response.data.tokens.refresh;
    if (refreshToken) {
      TokenStorage.saveRefreshToken(refreshToken);
    }

    // Update Axios to use the token for each request by default.
    this.setAxiosClientDefaultAuthHeader(accessToken);
  }

  private static onFailedAuth(error: any) {
    TokenStorage.clear();
    DialogService.warning({
      message: 'Session has expired, you will be redirected to the login page',
      onConfirm: () => {
        // Force logout because Fusion can automatically log the user back (Fusion's session)
        SecurityService.logout();
      }
    });
    return Promise.reject(error);
  }

  private static setAxiosClientDefaultAuthHeader(accessToken: string | null) {
    axios.defaults.headers.common[AUTHORIZATION] = `Bearer ${accessToken}`;
  }

  private static setRequestAuthHeader(requestConfig: AxiosRequestConfig, accessToken: string | null) {
    requestConfig.headers[AUTHORIZATION] = `Bearer ${accessToken}`;
  }

  private static isUnauthorized(error: AxiosError) {
    if (error.response) {
      return error.response.status === 401;
    }
    return false;
  }
}

export default SecurityService;
